import { Duration, intervalToDuration } from 'date-fns'
import { atom, useAtom } from 'jotai'
import { useCallback, useEffect } from 'react'
import { useUserState } from '~/atoms/UserState'
import {
  useGetAgentStatus,
  useGetUserAgentStatus,
  useQueueLogin,
  useQueueLogout,
  useUpdateUserAgentStatus,
} from '~/queries/queueQueries'
import { Availability } from '~/types/AgentCustomStatus'

const STATUS_REFRESH_INTERVAL_MS = 1000 * 30

export const enum QueueAgentStatus {
  InQueue = 'IN_QUEUE',
  OutOfQueue = 'OUT_OF_QUEUE',
  DoNotDisturb = 'DO_NOT_DISTURB',
  Offline = 'OFFLINE',
}

export interface CustomQueueStatus {
  id: number
  label: string
  availability: Availability
  startTime: Date
}

const defaultStatus = QueueAgentStatus.OutOfQueue
const currentAgentStatusAtom = atom<QueueAgentStatus>(defaultStatus)
const currentCustomStatusAtom = atom<CustomQueueStatus | undefined>(undefined)
const elapsedTimeAtom = atom<Duration | undefined>(undefined)
const formattedTimeAtom = atom<string | undefined>(undefined)
const inQueueStartTimeAtom = atom<Date | undefined>(undefined)
const pendingStatusUpdateAtom = atom<boolean>(false)

// Set up global refresh if not already running
const setupGlobalStatusRefresh = (refreshFn: () => void) => {
  if (globalThis.queueStatusInterval) return // Already set up

  // Initial fetch
  refreshFn()

  globalThis.queueStatusInterval = setInterval(() => {
    refreshFn()
  }, STATUS_REFRESH_INTERVAL_MS)
}

export const useCallQueue = () => {
  const [currentAgentStatus, setCurrentAgentStatus] = useAtom(
    currentAgentStatusAtom,
  )
  const [currentCustomStatus, setCurrentCustomStatus] = useAtom(
    currentCustomStatusAtom,
  )
  const [elapsedTime, setElapsedTime] = useAtom(elapsedTimeAtom)
  const [formattedTime, setFormattedTime] = useAtom(formattedTimeAtom)
  const [inQueueStartTime, setInQueueStartTime] = useAtom(inQueueStartTimeAtom)
  const [isPendingUpdate, setIsPendingUpdate] = useAtom(pendingStatusUpdateAtom)

  const { currentUserExtension } = useUserState()

  const queueLogin = useQueueLogin({ extensionId: currentUserExtension?.id })
  const queueLogout = useQueueLogout({ extensionId: currentUserExtension?.id })
  const updateUserAgentStatus = useUpdateUserAgentStatus({})

  useEffect(() => {
    const startTime = currentCustomStatus?.startTime || inQueueStartTime
    if (
      !startTime ||
      (currentAgentStatus !== QueueAgentStatus.InQueue && !currentCustomStatus)
    )
      return

    const updateElapsedTime = () => {
      setElapsedTime(
        intervalToDuration({
          start: startTime,
          end: new Date(),
        }),
      )
    }

    updateElapsedTime()
    const interval = setInterval(updateElapsedTime, 1000)

    return () => clearInterval(interval)
  }, [currentCustomStatus, currentAgentStatus, inQueueStartTime])

  useEffect(() => {
    if (!elapsedTime) return

    setFormattedTime(
      elapsedTime.hours && elapsedTime.hours > 0
        ? `${String(elapsedTime.hours).padStart(2, '0')}:${String(
            elapsedTime.minutes ?? 0,
          ).padStart(
            2,
            '0',
          )}:${String(elapsedTime.seconds ?? 0).padStart(2, '0')}`
        : `${String(elapsedTime.minutes ?? 0).padStart(2, '0')}:${String(
            elapsedTime.seconds ?? 0,
          ).padStart(2, '0')}`,
    )
  }, [elapsedTime])

  const { refetch: refreshAgentStatus } = useGetAgentStatus({
    extensionId: currentUserExtension?.id,
  })

  const { refetch: refreshUserAgentStatus } = useGetUserAgentStatus({
    ext_uuid: currentUserExtension?.uuid,
  })

  const refreshStatuses = useCallback(async () => {
    if (isPendingUpdate) return

    const agentStatus = await refreshAgentStatus()
    const userAgentStatus = await refreshUserAgentStatus()

    console.log('new agentStatus', agentStatus)
    console.log('new userAgentStatus', userAgentStatus)

    if (!agentStatus || !userAgentStatus) return

    if (agentStatus.status === 'Available') {
      setCurrentAgentStatus(QueueAgentStatus.InQueue)
      setCurrentCustomStatus(undefined)
      if (userAgentStatus.updated_at) {
        setInQueueStartTime(new Date(userAgentStatus.updated_at))
      } else {
        setInQueueStartTime(new Date())
      }
    } else if (
      agentStatus.status === 'Logged Out' &&
      !userAgentStatus.agent_status
    ) {
      setCurrentAgentStatus(QueueAgentStatus.OutOfQueue)
      setCurrentCustomStatus(undefined)
      setInQueueStartTime(undefined)
    } else if (userAgentStatus.agent_status) {
      setCurrentAgentStatus(
        userAgentStatus.agent_status.id as unknown as QueueAgentStatus,
      )
      setCurrentCustomStatus({
        id: userAgentStatus.agent_status.id,
        label: userAgentStatus.agent_status.name,
        availability: userAgentStatus.agent_status.availability,
        startTime: new Date(userAgentStatus.updated_at),
      })
    }
  }, [refreshAgentStatus, refreshUserAgentStatus, isPendingUpdate])

  useEffect(() => {
    setupGlobalStatusRefresh(() => {
      refreshStatuses()
    })
  }, [])

  const updateStatus = async (
    status: QueueAgentStatus,
    customStatus?: CustomQueueStatus,
  ) => {
    setCurrentAgentStatus(status)
    setCurrentCustomStatus(customStatus)
    setIsPendingUpdate(true)

    try {
      switch (status) {
        case QueueAgentStatus.InQueue:
          await queueLogin()
          await updateUserAgentStatus(null, currentUserExtension?.uuid, true)
          setInQueueStartTime(new Date())
          break
        case QueueAgentStatus.Offline:
          await queueLogout()
          await updateUserAgentStatus(null, currentUserExtension?.uuid, false)
          break
        default:
          await queueLogout()
          if (customStatus) {
            await updateUserAgentStatus(
              customStatus.id,
              currentUserExtension?.uuid,
              false,
            )
          }
          setInQueueStartTime(undefined)
          break
      }
    } finally {
      setIsPendingUpdate(false)
    }
  }

  return {
    currentAgentStatus,
    currentCustomStatus,
    formattedTime,
    updateStatus,
  }
}

declare global {
  // eslint-disable-next-line no-var
  var queueStatusInterval: NodeJS.Timeout | undefined
}
